import { useEffect, useRef, useState } from "react"
import { observer } from "mobx-react"
import {
  Page,
  Header,
  PureLogo,
  useStore,
  DocumentTemplate,
  Footer,
} from "@features/common"
import {
  FaqLogo,
  DesktopFaqTableOfContents,
  TouchFaqTableOfContents,
  FaqDetails,
} from "@features/faq"
import { SearchForm } from "@features/search"
import { serverSidePagePrepare, useToggle } from "@lib/hooks"
import { IconSearch, IconHamburger } from "@lib/icons"
import { scrollToTop } from "@lib/scroll"
import { RouterLink } from "@lib/routing"
import { useRouter } from "next/router"

export const getServerSideProps = serverSidePagePrepare({
  callback: async ({ Models }) => {
    await Models.GlobalModel.setLang("zh", true)
    await Models.FaqModel.init("zh")
  },
})

const FaqPage = observer(function FaqPage() {
  const {
    FaqModel: {
      data,
      dataWithSearchHighlighting,
      getTextByKey,
      tableOfContents,
      saveSearchQuery,
    },
    AnalyticsModel: { faqSearchOpenerClickHandler },
    GlobalModel: { openMenu, accessLanguages },
  } = useStore()
  const { opened, open, close } = useToggle()
  const { asPath } = useRouter()
  const headerRef = useRef<HTMLDivElement | null>(null)
  const [isMounted, setIsMounted] = useState(false)
  const languages = [...accessLanguages, "zh", "es"]

  function handleSearchOpenerClick() {
    faqSearchOpenerClickHandler()
    open()
  }

  // TODO: плохая идея здесь сбрасывать даные, но тащить в модель состояние открытости/закрытости формы не хочу
  function closeSearchFormHandler() {
    // @ts-ignore
    dataWithSearchHighlighting.clear()
    data.forEach((item) => dataWithSearchHighlighting.push(item))
    close()
  }

  function setHeaderYOffset() {
    if (headerRef.current) {
      headerRef.current.style.top = window.pageYOffset + "px"
      const aside = document.querySelector("aside")
      if (aside) {
        aside.style.top =
          window.pageYOffset +
          headerRef.current.getBoundingClientRect().height +
          "px"
      }
    }
  }

  useEffect(() => {
    document.addEventListener("scroll", setHeaderYOffset)
    setIsMounted(true)

    return function cleanup() {
      document.removeEventListener("scroll", setHeaderYOffset)
    }
  }, [])

  useEffect(() => {
    function scrollToAnchor() {
      const hash = asPath.split("#")[1]
      if (hash) {
        const section = document.querySelector(`[data-faq-id=${hash}]`)
        if (section) {
          section.scrollIntoView()
        }
      }
    }
    if (isMounted) {
      setTimeout(() => scrollToAnchor(), 100)
    }
  }, [isMounted, asPath])

  const title = "FAQ"
  const metaTitle = getTextByKey("metaTitle") || title
  const description = getTextByKey("metaDescription")
  const seoH1 = getTextByKey("seoH1.title")

  return (
    <Page
      meta={{
        FAQData: data,
        title,
        metaTitle,
        description,
      }}
      headerRef={headerRef}
      header={
        opened ? (
          <SearchForm
            onClose={closeSearchFormHandler}
            onBlur={saveSearchQuery}
            placeholder={getTextByKey("search.search_field.placeholder")}
          />
        ) : (
          <Header
            theme="dark"
            logo={
              <RouterLink
                uri="/faq"
                forceLang="zh"
                onClick={() => scrollToTop()}
                data-test-id="header__pure_logo"
                aria-label="root"
                className="mt-4 mb-3"
              >
                <div className="flex space-x-2 tablet-s:space-x-4">
                  <PureLogo className="text-white h-4 w-auto my-1 tablet:w-auto tablet-s:h-7 tablet:h-7 tablet-s:my-1.5" />
                  <FaqLogo />
                </div>
              </RouterLink>
            }
            languageSwitcher={null}
            navigationOpener={
              <button
                onClick={openMenu}
                data-test-id="header__menu_button"
                aria-label="menu"
              >
                <IconHamburger className="w-8 h-8 text-white" />
              </button>
            }
            searchOpener={
              <button className="inline-flex focus:outline-none focus:ring-0">
                <IconSearch
                  className="w-8 h-8 text-white"
                  onClick={handleSearchOpenerClick}
                />
              </button>
            }
          />
        )
      }
      alternativeHeader
      seoH1={seoH1}
      footer={<Footer accessLanguages={languages} />}
    >
      <div
        className="container pt-5 pb-15 laptop:pt-8 laptop:pb-20"
        id="faq_page"
      >
        <TouchFaqTableOfContents />
        <DocumentTemplate
          sidebar={
            tableOfContents.length ? <DesktopFaqTableOfContents /> : null
          }
        >
          <section className="space-y-10" id="scrollContainerElement">
            {dataWithSearchHighlighting.map((item, index) => (
              <FaqDetails
                key={`faq-${item.id}`}
                id={`faq-${item.id}`}
                data={item}
                ordinalNumber={index + 1}
              />
            ))}
          </section>
        </DocumentTemplate>
      </div>
    </Page>
  )
})

export default FaqPage
